/* ============================================================================
   MODELES — diseno base por producto (FEATURE 0)
   ----------------------------------------------------------------------------
   Un producto puede llevar un `modeleDefaut`: un diseno que ya esta puesto
   cuando el cliente abre el Studio. Segun `modePersonnalisation`:

     "libre"  -> Studio completo de siempre; el modelo es solo un punto de
                 partida y el cliente puede tocarlo todo.
     "simple" -> el cliente SOLO edita lo que Franklin haya marcado como
                 editable en cada elemento. El resto no es ni seleccionable.

   POR QUE EL MODELO SE GUARDA EN FORMATO FABRIC: podria haberse inventado un
   formato propio, pero entonces habria que escribir su instanciacion, su
   serializacion y su interaccion con el historial y los clipPaths. Guardarlo
   como lo que produce canvas.toJSON(PROPS) permite sembrarlo con el mismo
   loadFromJSON que ya se usa al cambiar de vista: cero maquinaria nueva.

   `baseImprimee` distingue dos realidades de produccion:
     true  -> el diseno base YA viene impreso en el producto fisico, asi que el
              archivo de impresion lleva SOLO lo que anadio el cliente.
     false -> el archivo lleva base + cliente, todo junto.

   Emplacements: huecos que el cliente rellena. `media:'photo'` es un marco al
   que sube UNA foto, que se encuadra dentro sin poder mover el marco;
   `media:'texte'` es un texto de contenido editable y todo lo demas fijo.
   ========================================================================== */

const MODELES = (() => {

  /* Que puede tocar el cliente en un elemento del modelo. true = BLOQUEADO.
     El defecto es restrictivo a proposito: en modo simple, un elemento sin
     permisos declarados no deberia poder moverse ni borrarse por descuido. */
  const VERROUS_DEFECTO = {
    contenu: true, police: true, couleur: true, position: true, suppression: true,
  };

  const perso        = p => (p && p.perso) || {};
  /* MODO SIMPLE DE VERDAD, NO SOLO DECLARADO.
     El modo « guiado » significa « el cliente solo toca lo que Franklin
     coloco »: se le quita « + Anadir un texto », y las pestanas Photos, Fond
     y Formes desaparecen. Perfecto... mientras haya algo colocado.

     Sin plantilla, ese mismo modo deja un Studio MUERTO: ni un texto que
     tocar, ni un boton para crear uno. Franklin se encontro exactamente eso
     en su porte-cles hexagonal — « aqui no me deja escribir » — y tenia toda
     la razon. El admin ya lo avisa como BLOQUEANTE, pero un aviso que se
     puede publicar igual no es una proteccion: es una nota.

     Asi que el modo simple exige las dos cosas. Sin plantilla se comporta
     como libre, que es utilizable, en vez de como guiado, que es nada. Un
     producto mal configurado tiene que salir raro, nunca inservible. */
  const esSimple = p => perso(p).modePersonnalisation === 'simple'
                     && aPlantilla(p);

  /* ¿La plantilla tiene ALGO en alguna vista? Un `modeleDefaut` presente pero
     con las vistas vacias engana igual que no tenerlo. */
  function aPlantilla(p) {
    const m = perso(p).modeleDefaut;
    return !!(m && Array.isArray(m.vues)
              && m.vues.some(v => Array.isArray(v.objects) && v.objects.length));
  }

  /* Lo que el admin DECLARO, sin la comprobacion: el aviso de configuracion
     necesita distinguir « no lo queria » de « lo queria y le falta algo ». */
  const modeDeclare = p => perso(p).modePersonnalisation || 'libre';
  const modeleDe     = p => perso(p).modeleDefaut || null;
  const baseImprimee = p => !!perso(p).baseImprimee;

  const verrousDe = o => Object.assign({}, VERROUS_DEFECTO, o && o.verrous);

  /* Semilla de una vista: los objetos del modelo, listos para loadFromJSON. */
  function objetosDeVue(produit, vueId) {
    const m = modeleDe(produit);
    if (!m || !Array.isArray(m.vues)) return null;
    const v = m.vues.find(x => x.vue === vueId);
    if (!v || !Array.isArray(v.objects) || !v.objects.length) return null;
    // copia profunda: el modelo de la config no debe mutar al editarlo
    return { objects: JSON.parse(JSON.stringify(v.objects)) };
  }

  /* Aplica los bloqueos a un objeto ya instanciado en el canvas.
     En modo libre no se bloquea nada: el modelo es solo un punto de partida. */
  function appliquerVerrous(o, produit) {
    if (!o || !o.estModele) return;
    if (!esSimple(produit)) {
      o.set({ selectable: true, evented: true, hasControls: true, lockMovementX: false,
              lockMovementY: false });
      return;
    }
    const v = verrousDe(o);
    const editable = !v.contenu || !v.couleur || !v.police;   // hay algo que tocar
    const movible  = !v.position;

    o.set({
      selectable: editable || movible,
      evented:    editable || movible,
      hasControls: movible,
      lockMovementX: !movible, lockMovementY: !movible,
      lockScalingX: !movible,  lockScalingY: !movible,
      lockRotation: !movible,
      hoverCursor: (editable || movible) ? 'move' : 'default',
    });
  }

  /**
   * ¿Puede el cliente cambiar ESTA propiedad de ESTE objeto?
   * ---------------------------------------------------------------------------
   * `appliquerVerrous` ya impedía mover y redimensionar: eso lo hace Fabric con
   * sus banderas. Pero el color, la tipografía y el contenido no pasan por
   * Fabric — se aplican desde los paneles del Studio, que no preguntaban nada.
   * Resultado: el admin declaraba « couleur : non » y el cliente cambiaba el
   * color igual. Una promesa que la tienda no cumplía.
   *
   * Fuera del modo « simple » no hay candados: el modelo es un punto de partida.
   */
  const PROP_VERROU = {
    fill: 'couleur', stroke: 'couleur',
    fontFamily: 'police', policeId: 'police',
    text: 'contenu',
  };
  function peutModifier(o, prop, produit) {
    if (!o || !o.estModele) return true;
    if (!esSimple(produit)) return true;
    const clave = PROP_VERROU[prop];
    if (!clave) return true;
    return !verrousDe(o)[clave];
  }

  /** Quita de un lote de propiedades las que este objeto tiene bajo llave. */
  function filtrerProps(o, props, produit) {
    const out = {}; let bloquees = 0;
    Object.keys(props || {}).forEach(k => {
      if (peutModifier(o, k, produit)) out[k] = props[k];
      else bloquees++;
    });
    return { props: out, bloquees };
  }

  /* Un emplacement vacio no debe llegar al archivo de impresion: es un hueco
     de la plantilla, no contenido. */
  const emplacementVacio = o => !!(o && o.emplacement && !o.emplacementRempli);

  /* Que elementos entran en el archivo de impresion. */
  function elementsAImprimer(objetos, produit) {
    const soloCliente = baseImprimee(produit);
    return objetos.filter(o => {
      if (emplacementVacio(o)) return false;
      if (soloCliente && o.estModele && !o.emplacementRempli) return false;
      return true;
    });
  }

  /* --- Emplacements -------------------------------------------------------
     Un emplacement es un hueco de la plantilla que el cliente rellena. El
     MARCO es fijo: en modo simple el cliente no lo mueve ni lo borra, solo
     encuadra su contenido dentro.

     El marco vive en `emplacement` {x,y,w,h} en pixeles del mockup, y el
     encuadre del cliente en `cadrage` {zoom, dx, dy}, donde dx/dy son
     fracciones del marco. Guardar el encuadre aparte de la posicion del
     objeto es lo que permite regenerar la foto a 300 DPI: el export vuelve a
     encajar el ORIGINAL en el marco con ese mismo zoom y desplazamiento. */

  const CADRAGE_DEFECTO = { zoom: 1, dx: 0, dy: 0 };

  /* Escala que hace que la foto CUBRA el marco (cover), antes del zoom. */
  function escalaCover(marco, natW, natH) {
    return Math.max(marco.w / natW, marco.h / natH);
  }

  /* Posicion y escala finales de la foto dentro de su marco.
     dx/dy son fracciones del sobrante, asi el encuadre es independiente de la
     resolucion: vale igual en pantalla que a 300 DPI. */
  function encadrer(marco, natW, natH, cadrage) {
    const c = Object.assign({}, CADRAGE_DEFECTO, cadrage);
    const e = escalaCover(marco, natW, natH) * Math.max(1, c.zoom);
    const w = natW * e, h = natH * e;
    const sobraX = Math.max(0, w - marco.w) / 2;
    const sobraY = Math.max(0, h - marco.h) / 2;
    return {
      escala: e, w, h,
      cx: marco.x + marco.w / 2 - clamp(c.dx, -1, 1) * sobraX,
      cy: marco.y + marco.h / 2 - clamp(c.dy, -1, 1) * sobraY,
      sobraX, sobraY,
    };
  }

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v == null ? 0 : v));

  /* DPI real de la foto dentro de su marco: los pixeles del original que caen
     en el ancho del marco, llevados a cm. */
  function dpiEmplacement(marco, natW, natH, cadrage, cmParPx) {
    const f = encadrer(marco, natW, natH, cadrage);
    const cmAncho = marco.w * cmParPx;
    if (cmAncho <= 0) return null;
    return Math.round((marco.w / f.escala) / cmAncho * 2.54);
  }

  return { VERROUS_DEFECTO, CADRAGE_DEFECTO, esSimple, aPlantilla, modeDeclare,
           modeleDe, baseImprimee,
           verrousDe, peutModifier, filtrerProps,
           objetosDeVue, appliquerVerrous, emplacementVacio,
           elementsAImprimer, escalaCover, encadrer, dpiEmplacement, clamp };
})();
